/**
 * Supabase クライアント（ブラウザ側）。
 *
 * anon キーは公開値なので、ここに書いても秘密は漏れない。
 * 見えるデータの範囲は RLS が決める。
 * 「サインアップできただけのユーザーには何も見えない」ことは
 * supabase/migrations/20260817080000_rls_hardening.sql で担保している。
 */
import { createClient } from '@supabase/supabase-js'
import type { Role } from './roles.ts'

/**
 * 認証エラーをURLから拾っておく。
 *
 * マジックリンクが期限切れ・使用済みだと、Supabase は
 * `#error=access_denied&error_code=otp_expired&...` を付けて戻してくる。
 * ところが detectSessionInUrl は処理後にハッシュを消してしまうので、
 * **createClient より前に**退避しないと理由が画面に出せない。
 * （これが無いと、利用者にはただログイン画面が出るだけに見える。）
 */
function captureAuthError(): { code: string | null; description: string | null } | null {
  if (typeof window === 'undefined') return null
  const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  const fromQuery = new URLSearchParams(window.location.search)
  const pick = (k: string) => fromHash.get(k) ?? fromQuery.get(k)
  if (!pick('error') && !pick('error_code') && !pick('error_description')) return null
  return { code: pick('error_code') ?? pick('error'), description: pick('error_description') }
}

export const authUrlError = captureAuthError()

const url = import.meta.env.VITE_SUPABASE_URL
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!url || !anonKey) {
  throw new Error('VITE_SUPABASE_URL と VITE_SUPABASE_ANON_KEY を .env に設定してください')
}

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
})

export type AssetStatus = 'draft' | 'uploading' | 'processing' | 'published' | 'archived' | 'failed'

export type Asset = {
  id: string
  title: string
  description: string | null
  kind: string
  status: AssetStatus
  file_name: string | null
  byte_size: number | null
  sha1: string | null
  duration_ms: number | null
  width: number | null
  height: number | null
  stream_uid: string | null
  proxy_r2_key: string | null
  thumbnail_r2_key: string | null
  tags: string[]
  published_at: string | null
  category_id: string | null
  /** 元本の Box ファイルID。null なら Box に実体が無い（下書き）。 */
  box_file_id: string | null
  /** 詳細表示用プレビュー（R2）。写真の拡大表示に使う。 */
  poster_r2_key: string | null
  /** 撮影日時（EXIF 等から抽出）。 */
  captured_at: string | null
  /** メタデータ抽出が完了した時刻。null なら未抽出。 */
  metadata_extracted_at: string | null
  /** 技術メタデータ（コーデック・ビットレート等）。 */
  metadata: Record<string, unknown>
  /** カスタム項目の値 {key: value}。key は asset_fields.key。顧客にも見える。 */
  attributes: Record<string, unknown>
}

export type Category = {
  id: string
  name: string
  slug: string
  sort_order: number
  /** 親カテゴリ。null なら最上位。最大3階層（lib/categories.ts の CATEGORY_MAX_DEPTH）。 */
  parent_id: string | null
  box_folder_id: string
}

export type AssetFieldType = 'text' | 'number' | 'date' | 'boolean' | 'enum' | 'multi_enum'

export type FieldOption = { value: string; label: string }

/** カスタム項目の台帳（asset_fields）。key は機械IDで改名しない。 */
export type AssetField = {
  key: string
  label: string
  type: AssetFieldType
  options: FieldOption[]
  description: string | null
  searchable: boolean
  sort_order: number
}

/** カテゴリへの項目の割り当て（category_fields）。祖先の割り当ては配下に継承される。 */
export type CategoryField = {
  category_id: string
  field_key: string
  position: number
  required: boolean
}

export type Profile = {
  id: string
  email: string
  display_name: string | null
  role: Role
  org_id: string | null
}

export type License = {
  id: string
  asset_id: string | null
  collection_id: string | null
  usage_scope: string
  allows_master_download: boolean
  valid_until: string | null
  max_downloads: number | null
  status: string
}
